"use client";

import React, { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useGlobalModalStack } from "@wtc/ui";
import { DetailNavigationProvider } from "@/contexts/DetailNavigationContext";
import { UniversalDetailModalContainer } from "../UniversalDetailModalContainer";
import { useContactHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { useCompanyHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { getEntityDefinition } from "@/lib/entity-registry";
import { useRecurringTransactionHooksForPage } from "@/lib/hooks/api/quickbooks/recurring-transactions";
import type { CompanyEntity } from "@/lib/types/company";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import { extractEntityId } from "@/lib/modals";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";

/**
 * Renders contact, company, or recurring transaction detail modal when:
 * 1. Opened via modal stack with data (e.g. openModal("contacts-detail", { contactId }) from address map), or
 * 2. URL has modal=contact-detail / modal=company-detail / modal=recurring-transactions-detail on pages that are NOT the entity's own list page.
 * On /contacts, /companies, and /recurring-transactions the entity page already renders the detail modal via EntityPageModals.
 * 
 * UPDATED: Now uses the new type-safe modal URL system from @/lib/modals
 */
export function GlobalEntityDetailModal() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isModalOpen, getModalData } = useGlobalModalStack();
  // Defensive role gate: recurring transactions are an internal-only entity a
  // client_user must never open (CLIENT_USER_ACCESS_SCOPE — clients see only
  // tickets/time/MSP docs/reports). The route is already client-blocked
  // (ROLE_BASED_ROUTES) and no client surface links here; this stops a client
  // from rendering the modal via a stray URL/stack-open as well.
  const { isClientUser } = useRoleCheck();

  const contactHooks = useContactHooks();
  const companyHooks = useCompanyHooks();
  const recurringTransactionHooks = useRecurringTransactionHooksForPage();

  const contactModal = useMemo(() => {
    if (pathname === "/contacts") return null;
    
    const stackData = getModalData("contacts-detail") as { contactId?: string; contactsId?: string } | undefined;
    const contactId = 
      stackData?.contactsId || 
      stackData?.contactId || 
      extractEntityId("contacts-detail", searchParams);
    
    const fromStack = isModalOpen("contacts-detail") && contactId;
    const fromUrl = searchParams?.get("modal") === "contacts-detail" && contactId;
    
    if ((!fromStack && !fromUrl) || !contactId) return null;
    
    return {
      entityName: "contacts" as const,
      id: contactId,
      config: getEntityDefinition("contacts").tableConfig,
      hooks: contactHooks,
    };
  }, [pathname, searchParams, contactHooks, isModalOpen, getModalData]);

  const companyModal = useMemo(() => {
    if (pathname === "/companies") return null;
    
    const stackData = getModalData("companies-detail") as { companyId?: string; companiesId?: string } | undefined;
    const companyId = 
      stackData?.companiesId || 
      stackData?.companyId || 
      extractEntityId("companies-detail", searchParams);
    
    const fromStack = isModalOpen("companies-detail") && companyId;
    const fromUrl = searchParams?.get("modal") === "companies-detail" && companyId;
    
    if ((!fromStack && !fromUrl) || !companyId) return null;
    
    return {
      entityName: "companies" as const,
      id: companyId,
      config: getEntityDefinition("companies").tableConfig,
      hooks: companyHooks,
    };
  }, [pathname, searchParams, companyHooks, isModalOpen, getModalData]);

  const recurringTransactionModal = useMemo(() => {
    if (pathname === "/recurring-transactions") return null;
    // Client users may never open a recurring-transaction detail modal.
    if (isClientUser) return null;

    const stackData = getModalData("recurring-transactions-detail") as {
      recurringTransactionsId?: string;
      recurringTransactionId?: string;
    } | undefined;
    const recurringTransactionsId =
      stackData?.recurringTransactionsId ||
      stackData?.recurringTransactionId ||
      extractEntityId("recurring-transactions-detail", searchParams);
    
    const fromStack = isModalOpen("recurring-transactions-detail") && recurringTransactionsId;
    const fromUrl = searchParams?.get("modal") === "recurring-transactions-detail" && recurringTransactionsId;
    
    if ((!fromStack && !fromUrl) || !recurringTransactionsId) return null;
    
    return {
      entityName: "recurring-transactions" as const,
      id: recurringTransactionsId,
      config: getEntityDefinition("recurring_transactions").tableConfig,
      hooks: recurringTransactionHooks,
    };
  }, [pathname, searchParams, recurringTransactionHooks, isModalOpen, getModalData, isClientUser]);

  const contactItemIds = useMemo(
    () => (contactModal ? [contactModal.id] : []),
    [contactModal]
  );

  if (contactModal) {
    return (
      <DetailNavigationProvider>
        <UniversalDetailModalContainer
          modalId="contacts-detail"
          entityName="contacts"
          config={contactModal.config}
          hooks={contactModal.hooks}
          itemIds={contactItemIds}
          currentItemIndex={0}
        />
      </DetailNavigationProvider>
    );
  }

  if (companyModal) {
    return (
      <DetailNavigationProvider>
        <UniversalDetailModalContainer<CompanyEntity>
          modalId="companies-detail"
          entityName="companies"
          config={companyModal.config as any}
          hooks={companyModal.hooks}
          itemIds={[companyModal.id]}
          currentItemIndex={0}
        />
      </DetailNavigationProvider>
    );
  }

  if (recurringTransactionModal) {
    return (
      <DetailNavigationProvider>
        <UniversalDetailModalContainer<RecurringTransaction>
          modalId="recurring-transactions-detail"
          entityName="recurring-transactions"
          config={recurringTransactionModal.config as any}
          hooks={recurringTransactionModal.hooks}
          itemIds={[recurringTransactionModal.id]}
          currentItemIndex={0}
        />
      </DetailNavigationProvider>
    );
  }

  return null;
}
